import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { SnackbarService } from '@shared/components/snackbar/snackbar.service';
import { ActorService } from '@shared/actor/actor.service';
import { catchError, Observable, throwError } from 'rxjs';

const noRedirectForbiddenErrors = [
  'Wrong email address or password.',
  'Wrong email or password.'
];
const specialHeaders = {
  X_NO_REDIRECT: 'X-No-Redirect',
  X_FORBIDDEN_NO_REDIRECT: 'X-Forbidden-No-Redirect',
  X_CONFLICT_NO_REDIRECT: 'X-Conflict-No-Redirect',
  CONTENT_TYPE: 'Authorization'
};
const urlExceptions = ['https://s3.eu-west-1.amazonaws.com'];
const unauthorizedMessage = `You have been logged out due to inactivity. You will be redirected to the login page shortly.`;
const forbiddenMessage = `You don't have permissions to perform this action. Please check if your role has been updated.`;
const forbiddenNoRedirectMessage = `You don't have permissions to perform this action. Please check the information you have entered is correct.`;

export function ApiInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> {
  const snackbar = inject(SnackbarService);
  const router = inject(Router);
  const actorService = inject(ActorService);

  function handleUnauthorized(error: HttpErrorResponse) {
    snackbar.error(unauthorizedMessage);
    setTimeout(() => {
      actorService.logout();
    }, 4000);
    return throwError(() => error.message);
  }

  function handleForbidden(error: HttpErrorResponse, skipRedirect = false) {
    if (!skipRedirect && !noRedirectForbiddenErrors.includes(error?.error?.message)) {
      snackbar.error(forbiddenMessage);
      setTimeout(() => {
        actorService.getUserInfo();
        router.navigate(['/dashboard']);
      }, 4000);
    } else {
      snackbar.error(forbiddenNoRedirectMessage);
    }
    return throwError(() => error.message);
  }

  function handleConflict(error: HttpErrorResponse, requestHeaders: HttpHeaders) {
    if (requestHeaders.has(specialHeaders.X_CONFLICT_NO_REDIRECT)) {
      handleError(error);
    } else {
      const message = error.error.message || error.statusText;
      snackbar.error(message);
    }
    return throwError(() => error.message);
  }

  function handleNotFound(error: HttpErrorResponse, requestHeaders: HttpHeaders, url: string) {
    if (new RegExp(urlExceptions.join('|')).test(url)) {
      const message = error.error.message || error.statusText;
      snackbar.error(message);
    }
    // if (!requestHeaders.has(specialHeaders.X_NO_REDIRECT)) {
    //   router.navigate(['/dashboard']);
    // }
    return throwError(() => error.message);
  }

  function handleError(error: HttpErrorResponse) {
    const message = error.error.errors || error.error.message || error.statusText;
    snackbar.error(message, { duration: 5000 });

    if (error.error.context) {
      router.navigate([`/${error.error.context}`]);
    }
    return throwError(() => error.message);
  }

  return next(req).pipe(
    catchError((error) => {
      switch (error.status) {
        case 401:
          return handleUnauthorized(error);
        case 403:
          return handleForbidden(error, req.headers.has(specialHeaders.X_FORBIDDEN_NO_REDIRECT));
        case 404:
          return handleNotFound(error, req.headers, req.url);
        case 409:
          return handleConflict(error, req.headers);
        default:
          return handleError(error);
      }
    })
  );
}
